import type { JSX } from "solid-js";
import { createMemo, For, Show } from "solid-js";

// ─── StatusDot ─────────────────────────────────────────────────────────────

export type HostStatus = "online" | "idle" | "offline" | "pending" | "error";

const STATUS_LABEL: Record<HostStatus, string> = {
  online: "en ligne",
  idle: "inactif",
  offline: "hors ligne",
  pending: "en attente",
  error: "erreur",
};

export function StatusDot(props: { status: HostStatus; label?: boolean }) {
  return (
    <span class="status-dot" data-status={props.status} title={STATUS_LABEL[props.status]}>
      {props.label && <span style={{ "margin-left": "5px" }}>{STATUS_LABEL[props.status]}</span>}
    </span>
  );
}

// ─── HealthBar ─────────────────────────────────────────────────────────────

export function HealthBar(props: { value: number | null; width?: number }) {
  const pct = () => (props.value === null ? null : Math.round(props.value * 100));
  const level = () => {
    const value = pct();
    if (value === null) return "danger";
    return value >= 95 ? "ok" : value >= 80 ? "warn" : "danger";
  };

  return (
    <Show
      when={pct() !== null}
      fallback={
        <span class="health-bar-pct" style={{ color: "var(--text-4)" }}>
          —
        </span>
      }
    >
      <div class="health-bar" style={{ "max-width": props.width ? `${props.width + 40}px` : undefined }}>
        <div class="health-bar-track" style={{ width: `${props.width ?? 60}px` }}>
          <div class="health-bar-fill" data-level={level()} style={{ width: `${pct()}%` }} />
        </div>
        <span class="health-bar-pct">{pct()}%</span>
      </div>
    </Show>
  );
}

// ─── Sparkline ─────────────────────────────────────────────────────────────

export function Sparkline(props: { seed?: number; values?: number[]; width?: number; height?: number }) {
  const w = () => props.width ?? 60;
  const h = () => props.height ?? 16;

  const bars = createMemo(() => {
    if (props.values) return props.values.slice(-14);
    const n = 14;
    const bars: number[] = [];
    let s = (props.seed ?? 1) % 100000;
    for (let i = 0; i < n; i++) {
      s = (s * 9301 + 49297) % 233280;
      bars.push(0.15 + (s / 233280) * 0.85);
    }
    return bars;
  });

  const barW = () => w() / bars().length;

  return (
    <svg class="sparkline" width={w()} height={h()} aria-hidden="true">
      <For each={bars()}>
        {(v, i) => (
          <rect
            x={i() * barW() + 0.5}
            y={h() - v * h()}
            width={barW() - 1.5}
            height={v * h()}
            fill="var(--text-4)"
            rx="1"
          />
        )}
      </For>
    </svg>
  );
}

// ─── KpiCard ───────────────────────────────────────────────────────────────

export function KpiCard(props: { label: string; value: string; sub?: string; level?: "ok" | "warn" | "danger" }) {
  return (
    <div class="kpi-card" data-level={props.level}>
      <div class="kpi-label">{props.label}</div>
      <div class="kpi-value">{props.value}</div>
      {props.sub && <div class="kpi-sub">{props.sub}</div>}
    </div>
  );
}

// ─── Tag ───────────────────────────────────────────────────────────────────

export function Tag(props: { children: JSX.Element; variant?: "ok" | "warn" | "danger" | "info" | "elev" }) {
  return <span class={`tag${props.variant ? ` tag--${props.variant}` : ""}`}>{props.children}</span>;
}

// ─── PanelChrome ───────────────────────────────────────────────────────────

export function PanelChrome(props: {
  icon: string;
  title: string;
  subtitle?: string;
  version?: string;
  tagType?: "core" | "helper" | "staging";
  tagLabel?: string;
  actions?: JSX.Element;
  children: JSX.Element;
}) {
  return (
    <div class="panel-chrome">
      <div class="panel-chrome-head">
        <div class="panel-chrome-icon">{props.icon}</div>
        <div>
          <span class="panel-chrome-title">{props.title}</span>
          {props.subtitle && (
            <span class="panel-chrome-subtitle" style={{ "margin-left": "8px" }}>
              {props.subtitle}
            </span>
          )}
        </div>
        {props.version && <span class="panel-chrome-ver">v{props.version}</span>}
        {props.tagType && (
          <span class="panel-chrome-tag" data-type={props.tagType}>
            {props.tagLabel ?? props.tagType}
          </span>
        )}
        <div class="panel-chrome-actions">{props.actions}</div>
      </div>
      <div class="panel-chrome-body">{props.children}</div>
    </div>
  );
}

// ─── Layer badges ──────────────────────────────────────────────────────────

export function LayerBadges(props: { layers: string[] }) {
  return (
    <span style={{ display: "flex", gap: "3px", "flex-wrap": "wrap" }}>
      <For each={props.layers}>
        {(l) => (
          <span
            class="tag"
            style={{ "font-size": "10px", padding: "0 4px", "text-transform": "uppercase", "letter-spacing": ".04em" }}
          >
            {l}
          </span>
        )}
      </For>
    </span>
  );
}
